import { useState, useEffect, useRef } from 'react'
import { useSearchParams } from 'react-router-dom'
import AnalysisSourcePicker, { type AnalysisSource } from '../components/AnalysisSourcePicker'

type ChatSource = { type: 'dataset'; id: string; name: string } | { type: 'bookmark_folder'; id: string; name: string }
type SourceStatus = 'not_found' | 'running' | 'complete' | 'error' | 'stale' | 'unknown'

const DEFAULT_PICKER_VALUE: AnalysisSource = { type: 'dataset', datasetId: '', datasetName: '' }

function fromAnalysisSource(a: AnalysisSource): ChatSource | null {
  if (a.type === 'dataset' && a.datasetId) {
    return { type: 'dataset', id: a.datasetId, name: a.datasetName || '' }
  }
  if (a.type === 'bookmark_folder' && a.folderId) {
    return { type: 'bookmark_folder', id: String(a.folderId), name: a.folderName }
  }
  return null
}

interface ChatMessage {
  type: 'user' | 'assistant' | 'system' | 'error' | 'retrieval' | 'assistant_chunk' | 'assistant_start' | 'assistant_end'
  content?: string
  docs?: RetrievedDoc[]
  retrieved_pmids?: string[]
  similarity_scores?: number[]
}

interface RetrievedDoc {
  pmid: string
  text: string
  title?: string | null
  authors?: string[]
  journal?: string | null
  year?: number | null
  score: number
}

const STORAGE_KEY = 'pubmed_chat_history'

function storageKey(searchId: string, source: ChatSource | null): string {
  if (!source) return searchId
  return `${searchId}:${source.type}:${source.id}`
}

function loadChatHistory(searchId: string, source: ChatSource | null): ChatMessage[] {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (!stored) return []
    const data = JSON.parse(stored)
    return data[storageKey(searchId, source)] || []
  } catch {
    return []
  }
}

function saveChatHistory(searchId: string, messages: ChatMessage[], source: ChatSource | null) {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    const data = stored ? JSON.parse(stored) : {}
    data[storageKey(searchId, source)] = messages
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data))
  } catch {
    // Ignore storage errors
  }
}

function clearChatHistory(searchId: string, source: ChatSource | null) {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (!stored) return
    const data = JSON.parse(stored)
    delete data[storageKey(searchId, source)]
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data))
  } catch {
    // Ignore storage errors
  }
}

// Helper to get auth headers for fetch
function getAuthHeaders(): HeadersInit {
  const token = localStorage.getItem('user_token')
  return token ? { 'Authorization': `Bearer ${token}` } : {}
}

export default function ChatPage() {
  const [searchParams] = useSearchParams()
  const searchId = searchParams.get('search_id')
  
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [input, setInput] = useState('')
  const [isConnected, setIsConnected] = useState(false)
  const [isStreaming, setIsStreaming] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [topK, setTopK] = useState(5)
  const wsRef = useRef<WebSocket | null>(null)
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const [currentResponse, setCurrentResponse] = useState('')

  // Source: dataset or bookmark folder (RAG no longer runs on bare search).
  // The picker manages its own dataset/folder lists; we just track status per source.
  const [pickerValue, setPickerValue] = useState<AnalysisSource>(DEFAULT_PICKER_VALUE)
  const source: ChatSource | null = fromAnalysisSource(pickerValue)
  const [statuses, setStatuses] = useState<Record<string, SourceStatus>>({})

  const sourceKey = (s: ChatSource): string => `${s.type}:${s.id}`
  const currentStatus: SourceStatus = source ? (statuses[sourceKey(source)] || 'not_found') : 'unknown'

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }

  useEffect(() => {
    scrollToBottom()
  }, [messages, currentResponse])

  // Refetch index status whenever the selected source changes.
  useEffect(() => {
    if (source) checkStatus(source)
  }, [source?.type, source?.id])

  const checkStatus = async (s: ChatSource) => {
    const url = s.type === 'dataset'
      ? `/api/rag/dataset-status/${s.id}`
      : `/api/rag/bookmark-folder-status/${s.id}`
    try {
      const res = await fetch(url, { headers: getAuthHeaders() })
      const data = await res.json()
      setStatuses(prev => ({ ...prev, [sourceKey(s)]: (data.status as SourceStatus) || 'not_found' }))
    } catch {
      setStatuses(prev => ({ ...prev, [sourceKey(s)]: 'error' }))
    }
  }

  // Load chat history when source changes
  useEffect(() => {
    if (searchId) {
      setMessages(loadChatHistory(searchId, source))
    } else {
      setMessages([])
    }
  }, [searchId, source?.type, source?.id])

  // Save chat history whenever messages change
  useEffect(() => {
    if (searchId && messages.length > 0) {
      saveChatHistory(searchId, messages, source)
    }
  }, [messages, searchId, source?.type, source?.id])

  // Build (or rebuild) RAG index for the current source
  const buildIndex = async () => {
    if (!source) return
    const url = source.type === 'dataset'
      ? `/api/rag/dataset/${source.id}`
      : `/api/rag/bookmark-folder/${source.id}`
    setStatuses(prev => ({ ...prev, [sourceKey(source)]: 'running' }))
    try {
      const res = await fetch(url, { method: 'POST', headers: getAuthHeaders() })
      const data = await res.json()
      if (data.status === 'complete' || data.status === 'running') {
        const interval = setInterval(async () => {
          await checkStatus(source)
          const s = statuses[sourceKey(source)]
          if (s === 'complete' || s === 'error') clearInterval(interval)
        }, 3000)
        setTimeout(() => clearInterval(interval), 120000)
      } else if (data.detail) {
        setError(data.detail)
      }
    } catch (e: any) {
      setError(e.message)
      setStatuses(prev => ({ ...prev, [sourceKey(source)]: 'error' }))
    }
  }

  // Connect WebSocket only when source has a fresh, complete index
  useEffect(() => {
    if (!searchId || !source) return
    if (currentStatus !== 'complete') return

    const token = localStorage.getItem('user_token')
    const params = new URLSearchParams()
    if (token) params.append('token', token)
    if (source.type === 'dataset') params.append('dataset_id', source.id)
    if (source.type === 'bookmark_folder') params.append('bookmark_folder_id', source.id)
    const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
    const wsUrl = `${wsProtocol}//${window.location.host}/ws/chat/${searchId}?${params.toString()}`
    
    const ws = new WebSocket(wsUrl)
    wsRef.current = ws

    ws.onopen = () => {
      setIsConnected(true)
      setError(null)
    }

    ws.onmessage = (event) => {
      const data = JSON.parse(event.data)
      
      if (data.type === 'system') {
        setMessages(prev => [...prev, { type: 'system', content: data.content }])
      } else if (data.type === 'user') {
        setMessages(prev => [...prev, { type: 'user', content: data.content }])
      } else if (data.type === 'assistant_start') {
        setIsStreaming(true)
        setCurrentResponse('')
      } else if (data.type === 'assistant_chunk') {
        setCurrentResponse(prev => prev + data.content)
      } else if (data.type === 'assistant_end') {
        setIsStreaming(false)
        setMessages(prev => [...prev, {
          type: 'assistant',
          content: data.content || currentResponse,
          retrieved_pmids: data.retrieved_pmids,
          similarity_scores: data.similarity_scores
        }])
        setCurrentResponse('')
      } else if (data.type === 'retrieval') {
        setMessages(prev => [...prev, { type: 'retrieval', docs: data.docs }])
      } else if (data.type === 'error') {
        const errMsg = data.content || ''
        if (errMsg.includes('API key') || errMsg.includes('401')) {
          setError(errMsg + ' Go to Admin > API Keys to configure your DeepSeek API key.')
        } else {
          setError(errMsg)
        }
        setIsStreaming(false)
      }
    }

    ws.onerror = () => {
      setError('WebSocket error')
      setIsConnected(false)
    }

    ws.onclose = () => {
      setIsConnected(false)
    }

    return () => {
      ws.close()
    }
  }, [searchId, source?.type, source?.id, currentStatus])

  const sendMessage = () => {
    if (!input.trim() || !wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) return
    
    wsRef.current.send(JSON.stringify({
      message: input.trim(),
      top_k: topK
    }))
    setInput('')
    setError(null)
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      sendMessage()
    }
  }

  const handleClearChat = () => {
    if (searchId) {
      clearChatHistory(searchId, source)
    }
    setMessages([])
  }

  if (!searchId) {
    return (
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <h2 className="text-2xl font-bold mb-4">AI Chat</h2>
        <p className="text-gray-600">No search selected. Go to Search and select a session to start chatting.</p>
      </div>
    )
  }

  return (
    <div className="flex flex-col h-[calc(100vh-200px)]">
      <div className="bg-white rounded-lg shadow-sm border p-4 mb-4">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold">AI Research Assistant</h2>
            <p className="text-sm text-gray-600">Search ID: {searchId}</p>
          </div>
          <div className="flex items-center gap-3">
            {!source ? (
              <span className="px-3 py-1 bg-gray-100 text-gray-600 rounded-full text-sm font-medium">
                Pick a source
              </span>
            ) : currentStatus === 'complete' ? (
              <span className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm font-medium">
                RAG Ready
              </span>
            ) : currentStatus === 'running' ? (
              <span className="px-3 py-1 bg-yellow-100 text-yellow-800 rounded-full text-sm font-medium">
                Indexing...
              </span>
            ) : currentStatus === 'stale' ? (
              <button
                onClick={buildIndex}
                className="px-4 py-2 bg-amber-600 text-white rounded-md text-sm hover:bg-amber-700"
              >
                Rebuild (stale)
              </button>
            ) : (
              <button
                onClick={buildIndex}
                className="px-4 py-2 bg-blue-600 text-white rounded-md text-sm hover:bg-blue-700"
              >
                Build RAG Index
              </button>
            )}
            <span className={`px-3 py-1 rounded-full text-sm font-medium ${
              isConnected
                ? 'bg-green-100 text-green-800'
                : 'bg-gray-100 text-gray-600'
            }`}>
              {isConnected ? 'Connected' : 'Disconnected'}
            </span>
          </div>
        </div>

        {currentStatus === 'stale' && (
          <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-md text-sm text-amber-800">
            The underlying {source?.type === 'dataset' ? 'dataset' : 'bookmark folder'} has changed since the RAG index was built.
            Rebuild the index to chat against the current contents.
          </div>
        )}

        {/* Source picker — same component used on the AI Analysis page */}
        <div className="mt-3 pt-3 border-t">
          <AnalysisSourcePicker
            value={pickerValue}
            onChange={setPickerValue}
            searchId={searchId}
            allowedSources={['dataset', 'bookmark_folder']}
            heading="Chat about…"
            restrictedNotice="RAG chat runs on a saved dataset or bookmark folder so the index can be tied to a stable PMID set."
          />
          {source && (
            <p className="text-xs text-slate-500 mt-1">
              Index status:&nbsp;
              {currentStatus === 'complete' ? 'indexed ✓' :
               currentStatus === 'running' ? 'indexing…' :
               currentStatus === 'stale' ? 'stale — rebuild required' :
               'not indexed'}
            </p>
          )}
        </div>
        
        {/* Top-K Slider */}
        <div className="mt-3 pt-3 border-t flex items-center gap-4">
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-gray-700">Context Papers:</label>
            <span className="text-sm font-bold text-blue-600 w-6">{topK}</span>
          </div>
          <input
            type="range"
            min={5}
            max={50}
            step={1}
            value={topK}
            onChange={(e) => setTopK(parseInt(e.target.value))}
            className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
          />
          <div className="flex gap-2">
            <button
              onClick={handleClearChat}
              disabled={messages.length === 0}
              className="px-3 py-1 text-sm text-red-600 border border-red-200 rounded hover:bg-red-50 disabled:opacity-50"
            >
              Clear Chat
            </button>
          </div>
        </div>
      </div>

      <div className="flex-1 bg-white rounded-lg shadow-sm border overflow-y-auto p-4 space-y-4">
        {messages.length === 0 && (
          <div className="text-center text-gray-500 py-8">
            <p className="text-lg font-medium mb-2">Welcome to AI Research Assistant</p>
            <p>{source ? `Ask questions about the papers in your ${source.type === 'dataset' ? 'dataset' : 'bookmark folder'}.` : 'Pick a saved dataset or bookmark folder above to start chatting.'}</p>
            <p className="text-sm text-gray-400 mt-2">Chat history is saved per source.</p>
            {source && currentStatus !== 'complete' && (
              <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                <p className="text-sm text-yellow-800">
                  {currentStatus === 'not_found'
                    ? `No RAG index for this ${source.type === 'dataset' ? 'dataset' : 'bookmark folder'} yet — click Build above.`
                    : currentStatus === 'running'
                      ? 'RAG index is being built. Please wait...'
                      : currentStatus === 'stale'
                        ? 'The source has changed since indexing — rebuild required.'
                        : 'RAG index error. Please try rebuilding.'}
                </p>
              </div>
            )}
          </div>
        )}

        {messages.map((msg, idx) => (
          <div key={idx}>
            {msg.type === 'system' && (
              <div className="text-center text-sm text-gray-500 py-2">{msg.content}</div>
            )}
            
            {msg.type === 'user' && (
              <div className="flex justify-end">
                <div className="bg-blue-600 text-white rounded-lg px-4 py-2 max-w-2xl">
                  {msg.content}
                </div>
              </div>
            )}
            
            {msg.type === 'retrieval' && msg.docs && (
              <div className="bg-gray-50 rounded-lg p-3 border">
                <p className="text-xs font-medium text-gray-600 mb-2">Retrieved Papers ({msg.docs.length}):</p>
                <div className="space-y-2">
                  {msg.docs.map((doc, dIdx) => (
                    <div key={dIdx} className="text-sm border-l-2 border-blue-400 pl-2">
                      <p className="font-medium">{doc.title || 'Untitled'}</p>
                      <p className="text-xs text-gray-600">
                        {doc.authors?.join(', ')} • {doc.journal} ({doc.year}) • PMID: {doc.pmid}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">Score: {doc.score.toFixed(3)}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {msg.type === 'assistant' && (
              <div className="flex justify-start">
                <div className="bg-gray-100 rounded-lg px-4 py-2 max-w-3xl">
                  <div className="text-sm whitespace-pre-wrap">
                    {msg.content}
                  </div>
                  {msg.retrieved_pmids && (
                    <p className="text-xs text-gray-500 mt-2">
                      Based on {msg.retrieved_pmids.length} papers
                    </p>
                  )}
                </div>
              </div>
            )}
            
            {msg.type === 'error' && (
              <div className="text-center text-sm text-red-600 py-2">{msg.content}</div>
            )}
          </div>
        ))}
        
        {isStreaming && currentResponse && (
          <div className="flex justify-start">
            <div className="bg-gray-100 rounded-lg px-4 py-2 max-w-3xl">
              <div className="text-sm whitespace-pre-wrap">
                {currentResponse}
              </div>
              <span className="inline-block w-2 h-4 bg-blue-600 animate-pulse mt-1"></span>
            </div>
          </div>
        )}
        
        <div ref={messagesEndRef} />
      </div>

      <div className="mt-4 bg-white rounded-lg shadow-sm border p-4">
        {error && (
          <div className="mb-2 text-sm text-red-600">{error}</div>
        )}
        <div className="flex gap-2">
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={isConnected ? `Ask about the research... (using ${topK} papers)` : "Connect to chat..."}
            disabled={!isConnected || isStreaming}
            className="flex-1 border rounded-md px-3 py-2 resize-none h-20 disabled:bg-gray-50"
          />
          <button
            onClick={sendMessage}
            disabled={!isConnected || isStreaming || !input.trim()}
            className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed self-end"
          >
            Send
          </button>
        </div>
      </div>
    </div>
  )
}
